/* eslint-disable no-unsafe-optional-chaining */
import {TabBubbleUiInterface} from '@interface/index';
import {formatCompactNumber, getPokerValueAsText} from '@utils/index';
import useCommon from '@utils/hooks/common';
import {Button} from 'antd';
import classNames from 'classnames';
import dayjs from 'dayjs';
import {CardObj} from 'poker-interfaces/gameLogic/gameLogicInterface';
import React, {useEffect, useRef, useState} from 'react';
import styles from './styles';
import {
  deregisterTabBubbleRef,
  getTabBubbleRefForRoom,
  registerTabBubbleRef,
} from '@utils/tabBubbleRefs';

const TabBubble = (props: TabBubbleUiInterface) => {
  const {t, classes, system} = useCommon({
    withStyles: styles,
    withTranslation: ['game_room'],
    observers: ['system'],
  });
  const {index, bubbleData, isHighlighted, onBlur, onClick, onHover, roomCode} = props;
  const {
    hands,
    pot,
    displayMode,
    isSatOut,
    actionDuration,
    expireAt,
    isMyTurn,
    hasAlert,
    isTournament,
  } = bubbleData || {};

  const [timeBarPercentage, setTimeBarPercentage] = useState<number>(null);
  const [transitionDuration, setTransitionDuration] = useState<number>(0);

  const buttonRef = useRef<HTMLButtonElement>(null);
  const timerRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (roomCode && buttonRef.current) {
      registerTabBubbleRef(roomCode, buttonRef);
    }

    return () => {
      if (roomCode) {
        deregisterTabBubbleRef(roomCode);
      }
    };
  }, [roomCode, buttonRef.current]);

  useEffect(() => {
    if (isMyTurn && !isHighlighted) {
      const setTimeBar = (isSmoothTransition?: boolean) => {
        const remainingSeconds = dayjs(expireAt).diff(dayjs(), 'seconds', true);
        const percentageRemaining = Math.floor((remainingSeconds / actionDuration) * 100);
        setTimeBarPercentage(percentageRemaining);
        if (isSmoothTransition) {
          setTransitionDuration(1);
        }
      };
      setTransitionDuration(0);
      setTimeBar();
      timerRef.current = setInterval(() => setTimeBar(true), 1000);
    } else {
      setTransitionDuration(0);
      setTimeBarPercentage(0);
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [actionDuration, isMyTurn, expireAt, isHighlighted]);

  const getTimerStyle = () => {
    if (!bubbleData) {
      return null;
    }
    let borderRadius: string = null;
    let height: string = null;
    if (timeBarPercentage < 95 && timeBarPercentage >= 10) {
      borderRadius = '10px 0 0 10px';
      height = '100%';
    } else if (timeBarPercentage < 10) {
      const minHeightPercentage = 65;
      const heightDistance = 100 - minHeightPercentage;
      const percentageDistance = 10 - timeBarPercentage;
      const interpolate = 100 - (percentageDistance * 35) / 10;

      borderRadius = '10px 0 0 10px';
      height = `${interpolate}%`;
    } else {
      height = '100%';
      borderRadius = '10px';
    }

    let color: string = null;
    if (timeBarPercentage < 3) {
      color = 'transparent';
    } else if (timeBarPercentage < 33) {
      color = '#FF7766';
    } else if (timeBarPercentage < 66) {
      color = '#FBED21';
    } else {
      color = '#91F27F';
    }

    return {
      width: `${timeBarPercentage}%`,
      backgroundColor: color,
      borderRadius,
      height,
      transition: `${transitionDuration}s linear`,
    };
  };

  const getPokerSuitElement = (suit: CardObj['suit']) => {
    if (suit === 'SPADE') return <img src="/icon/game-room/spades.svg" />;
    if (suit === 'HEART') return <img src="/icon/game-room/hearts.svg" />;
    if (suit === 'CLUB') return <img src="/icon/game-room/clubs.svg" />;
    if (suit === 'DIAMOND') return <img src="/icon/game-room/diamonds.svg" />;
    return '';
  };

  const getCardInBubble = (card: CardObj, isVertical: boolean) => {
    const rank = getPokerValueAsText(card.rank);
    const suit = getPokerSuitElement(card.suit);
    return (
      <div
        className={classNames(
          isVertical && classes.verticalCard,
          system?.isGameOverlayShown && 'dynamicHeight'
        )}
      >
        <span className="rank">{rank}</span>
        <span className="suit">{suit}</span>
      </div>
    );
  };

  const getContent = () => {
    if (displayMode === 'POT') {
      let value: string;
      let prefix = '$';
      if (pot >= 10000) {
        value = formatCompactNumber(pot);
      } else {
        value = pot.toString();
      }

      if (isTournament) {
        prefix = '';
      }
      return `${prefix} ${value}`;
    }
    if (displayMode === 'SIT_OUT') {
      return isSatOut ? t('sitting_out') : pot;
    }
    if (displayMode === 'HANDS') {
      return (
        <div className={classNames(classes.bubbleHandsContainer)}>
          {hands.map(hand => getCardInBubble(hand, hands.length > 2))}
        </div>
      );
      // <>
      //   <span>{hand.rank}</span>
      //   <span>{ hand.suit}</span>
      // </>
    }
    return null;
  };

  return (
    <Button
      ref={buttonRef}
      onMouseEnter={onHover}
      className={classNames(
        classes.currentPotBox,
        isHighlighted && 'highlighted',
        hasAlert && 'hasAlert',
        displayMode === 'HANDS' && 'showingHands',
        system?.isGameOverlayShown && 'dynamicHeight'
      )}
      onBlur={onBlur}
      onClick={e => {
        e.preventDefault();
        system?.setBubbleAlert(index, false);
        if (getTabBubbleRefForRoom(roomCode)) {
          system?.dismissTournamentNotification(roomCode);
        }
        onClick(e);
      }}
    >
      {getContent()}
      <div
        style={{
          ...getTimerStyle(),
          ...{
            display: 'inline-block',
            position: 'absolute',
            left: '0',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: '-1',
          },
        }}
      />

      {isTournament && (
        <div className={classes.tournamentIcon}>
          <img height={10} width={10} src="/images/tournament/tab_bubble_icon.svg" />
        </div>
      )}
    </Button>
  );
};

export default TabBubble;
